/* Pure logic for the product-suggestion carousel (no React, no copy).
   Ported from carousel-sandbox (slides.js / data.js). All visible text lives in the i18n files (PC_* keys). */
import { getIngredientMonthlyPrice } from '../../../../../utils/productSuggestions';
import { RETAIL } from './retail';
import { claimNutrientPct, claimHits, focusIngredientNames } from './claims';

/* ---------- plans ---------- */
/* Discounts come from the totals in ProductSuggestion.jsx (monthly 10 %, 3-monthly 30 %, 90-day one-off 20 %).
   kit = € value of the free gifts (PlanGifts in MobileSubscriptionCardV4.jsx). One-off plans get no gifts. */
export const PLANS = {
  oneoff30: { discount: 0, kit: 0, subscription: false, gifts: [] },
  monthly: { discount: 10, kit: 20, subscription: true, gifts: ['container', 'guide'] },
  quarterly: { discount: 30, kit: 45, subscription: true, gifts: ['bag', 'container', 'guide', 'tracker'] },
  oneoff90: { discount: 20, kit: 0, subscription: false, gifts: [] },
};
export const GIFT_VALUES = { bag: 19, container: 11, guide: 9, tracker: 6 };
export const SHIPPING_VALUE = 6;

/* Plan duration strings used by useProduct → carousel plan key.
   Internal names differ from the labels: '3 months' is the MONTHLY Abo (30 pouches), '6 months' is the
   "every 3 months" Abo (90 pouches). See MobileSubscriptionCardV4.jsx. '1 month' is a legacy value. */
const DURATION_TO_PLAN = {
  '30 days': 'oneoff30',
  '1 month': 'monthly',
  '3 months': 'monthly',
  '6 months': 'quarterly',
  '90 days': 'oneoff90',
};
/* The page opens on the monthly Abo ('3 months') by default. */
export const planFromDuration = (duration) => DURATION_TO_PLAN[duration] || 'monthly';

const cheapestPlanKey = 'quarterly';

/* ---------- quiz codes ---------- */
const FOCUS_CODE = {
  36: 'energy',
  37: 'focus',
  38: 'sport',
  39: 'mood',
  40: 'sleep',
  41: 'stress',
  42: 'digestion',
  43: 'immune',
  44: 'skin',
  45: 'hair',
  46: 'nails',
  47: 'heart',
  48: 'eyes',
  49: 'muscle',
  50: 'bones',
  307: 'menopause',
};
export const PURPOSE_KEYS = ['feel_best', 'replace_supplements', 'specific_needs', 'fill_gaps', 'stop_pills'];

/* Ranked focus keys (max 3) from the ordered focusArea list of the recommendation page. */
export const focusKeysFromAreas = (focusAreas) =>
  (focusAreas || [])
    .map((area) => FOCUS_CODE[Number(area?.answerCode)])
    .filter(Boolean)
    .slice(0, 3);

/* ---------- number formatting ---------- */
export const fmtEur = (lang, v) => (lang === 'en' ? `€${v}` : `${v} €`);
export const fmtEur2 = (lang, v) => (lang === 'en' ? `€${v.toFixed(2)}` : `${v.toFixed(2).replace('.', ',')} €`);
export const fmtPct = (lang, v) => (lang === 'en' ? `${v}%` : `${v} %`);

/* ---------- slide 3: what you replace ----------
   Rows = retail products (D3 + K2, B-complex and probiotic are one product each).
   Omega-3 has no retail row and is excluded on both sides. */
const INGREDIENT_ROW = {
  28: 'd3k2',
  30: 'd3k2',
  12: 'mg',
  39: 'mg',
  31: 'zn',
  27: 'c',
  17: 'se',
  20: 'b12',
  34: 'b12',
  19: 'bcomplex',
  21: 'bcomplex',
  22: 'bcomplex',
  23: 'bcomplex',
  24: 'bcomplex',
  25: 'bcomplex',
  26: 'bcomplex',
  33: 'bcomplex',
  6: 'q10',
  1: 'ash',
  8: 'curcuma',
  11: 'iron',
  16: 'k',
  4: 'ca',
  18: 'a',
  29: 'e',
  5: 'cr',
  7: 'cu',
  13: 'mn',
  14: 'msm',
  35: 'probiotic',
  36: 'lutein',
  37: 'iodine',
};
const retailPerMonth = (r) => ((r.price / r.pack) * r.perDay * 30);

export const SLIDE3_ROWS = 5;
/* The price slide is skipped when the comparison is not meaningful. */
const MIN_PRODUCTS = 2;

const toDoseList = (ingredients) =>
  (ingredients || [])
    .map((i) => ({ id: Number(i?.ingredient_id), dose: Number(i?.daily_dose) }))
    .filter((i) => i.id > 0 && i.dose > 0);

export function priceCalc(ingredients, lang) {
  const plan = PLANS[cheapestPlanKey];
  const by = new Map();
  toDoseList(ingredients).forEach((i) => {
    const key = INGREDIENT_ROW[i.id];
    if (!key) return;
    by.set(key, (by.get(key) || 0) + getIngredientMonthlyPrice({ ingredient_id: i.id }, i.dose));
  });
  const items = RETAIL.filter((r) => by.has(r.key))
    .map((r, order) => ({ name: r.name[lang] || r.name.en, retail: retailPerMonth(r), riise: by.get(r.key), order }))
    .sort((a, b) => b.retail - a.retail || a.order - b.order);
  const retail = items.reduce((s, x) => s + x.retail, 0);
  const riiseRaw = items.reduce((s, x) => s + x.riise, 0) * (1 - plan.discount / 100);
  const result = {
    items,
    planKey: cheapestPlanKey,
    riiseRaw,
    retail: Math.round(retail),
    riise: Math.round(riiseRaw),
    diff: Math.round(retail - riiseRaw),
    count: items.length,
  };
  result.show = items.length >= MIN_PRODUCTS && result.diff > 0;
  return result;
}

/* ---------- slide 4: focus areas + authorised claims ---------- */
export function focusClaims(ingredients, focusKeys, lang, symptoms = []) {
  const list = toDoseList(ingredients);
  const doses = Object.fromEntries(list.map((i) => [i.id, i.dose]));
  const nameById = Object.fromEntries((ingredients || []).map((i) => [Number(i?.ingredient_id), i?.ingredient]));
  const pct = claimNutrientPct(doses);
  const rows = focusKeys.map((key) => {
    const res = claimHits(pct, key, symptoms);
    const names = focusIngredientNames(key, doses, lang, (id) => nameById[id]);
    return {
      key,
      res,
      ingredients: names,
      thin: res.kind !== 'symptoms' && res.hits.length < 3 && names.length > 0,
    };
  });
  return { rows };
}
